<template>
  <div class="certificats-modeles">
    <div class="cm-entete">
      <p class="text-grey mb-0">Textes proposés dans « Nouveau certificat médical ». Les champs entre crochets (patient, médecin, date…)
        sont remplis automatiquement ; le médecin relit et complète avant d’enregistrer.</p>
      <button type="button" class="btn btn-primary btn-radius" @click="ouvrir(null)"><i class="ic-plus h-13p"></i> Nouveau modèle</button>
    </div>

    <div v-if="erreur && !formulaire" class="alert alert-danger mt-3" role="alert">{{ erreur }}</div>
    <div v-if="chargement" class="text-grey mt-3">Chargement des modèles…</div>
    <p v-else-if="!modeles.length" class="text-grey mt-3">Aucun modèle de certificat.</p>
    <ul v-else class="cm-liste">
      <li v-for="(modele, index) in modeles" :key="modele.id">
        <div class="cm-infos">
          <strong>{{ modele.nom }}</strong>
          <span :class="modele.texte ? 'text-grey' : 'cm-vide'">{{ extrait(modele.texte) || 'Texte à rédiger par le médecin' }}</span>
        </div>
        <div class="cm-actions">
          <button type="button" class="cm-ordre" :disabled="index === 0 || occupe" aria-label="Monter" title="Monter" @click="deplacer(index, -1)">↑</button>
          <button type="button" class="cm-ordre" :disabled="index === modeles.length - 1 || occupe" aria-label="Descendre" title="Descendre" @click="deplacer(index, 1)">↓</button>
          <button type="button" class="btn btn-outline-primary btn-sm" @click="ouvrir(modele)">Modifier</button>
          <button type="button" :class="['btn btn-sm', aRetirer === modele.id ? 'btn-danger' : 'btn-outline-danger']" :disabled="occupe" @click="retirer(modele)">
            {{ aRetirer === modele.id ? 'Confirmer' : 'Retirer' }}</button>
        </div>
      </li>
    </ul>

    <div v-if="formulaire" class="cm-fenetre" role="dialog" aria-modal="true" aria-labelledby="cm-titre">
      <form class="cm-fenetre-contenu" novalidate @submit.prevent="enregistrer">
        <div class="cm-fenetre-entete">
          <h4 id="cm-titre" class="mb-0">{{ formulaire.id ? 'Modifier le modèle de certificat' : 'Nouveau modèle de certificat' }}</h4>
          <button type="button" class="btn btn-link cm-fermer" aria-label="Fermer" @click="fermer">×</button>
        </div>
        <div class="cm-corps">
          <div class="form-group"><label for="cm-nom">Nom du certificat *</label>
            <input id="cm-nom" v-model.trim="formulaire.nom" class="input-secondary-light" maxlength="150" placeholder="Ex. Certificat de repos médical"></div>
          <div class="cm-champs">
            <span>Insérer un champ automatique :</span>
            <button v-for="champ in champs" :key="champ.code" type="button" @click="insererChamp(champ.code)">{{ champ.libelle }}</button>
          </div>
          <div class="cm-editeur">
            <vue-editor ref="editeur" v-model="formulaire.texte" :editorToolbar="barreOutils" />
          </div>
          <p v-if="erreur" class="text-danger mt-2" role="alert">{{ erreur }}</p>
        </div>
        <div class="cm-pied">
          <button type="button" class="btn btn-secondary-light-o btn-radius" @click="fermer">Annuler</button>
          <button type="submit" class="btn btn-primary btn-radius" :disabled="enregistrement">{{ enregistrement ? 'Enregistrement…' : 'Enregistrer' }}</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor'
import { vueEditorToolbar } from '@/utils/constants'
import ModelesApi from '@/api/parametres/_certificats-modeles'
import { CHAMPS_CERTIFICAT, extraitCertificat } from '@/utils/certificat-champs'

function messageErreur(erreur, defaut) {
  const donnees = erreur && erreur.response ? erreur.response.data : null
  if (donnees && donnees.errors) {
    const premiers = Object.keys(donnees.errors).map(cle => donnees.errors[cle][0])
    if (premiers.length) return premiers[0]
  }
  return (donnees && donnees.message) || defaut
}

// Modèles de certificats médicaux pré-enregistrés (04/10/2026) : même éditeur que l'examen clinique.
export default {
  name: 'certificats-modeles',
  components: { VueEditor },
  data() {
    return {
      modeles: [], chargement: true, erreur: '', formulaire: null, enregistrement: false, occupe: false, aRetirer: null,
      champs: CHAMPS_CERTIFICAT,
      barreOutils: [...vueEditorToolbar, ['blockquote', 'code-block'], [{ script: 'sub' }, { script: 'super' }], ['link', 'clean']],
    }
  },
  mounted() {
    this.charger()
  },
  methods: {
    extrait(texte) {
      return extraitCertificat(texte, 140)
    },
    charger() {
      this.chargement = true
      return ModelesApi.liste().then(reponse => {
        this.modeles = Array.isArray(reponse) ? reponse : []
        this.erreur = ''
      }).catch(() => {
        this.erreur = 'Les modèles de certificats n’ont pas pu être chargés.'
      }).then(() => {
        this.chargement = false
      })
    },
    ouvrir(modele) {
      this.aRetirer = null
      this.erreur = ''
      this.formulaire = { id: modele ? modele.id : null, nom: modele ? modele.nom : '', texte: modele ? modele.texte || '' : '' }
    },
    fermer() {
      this.formulaire = null
      this.erreur = ''
    },
    insererChamp(code) {
      const editeur = this.$refs.editeur
      const quill = editeur ? editeur.quill : null
      if (!quill) {
        this.formulaire.texte = (this.formulaire.texte || '') + code
        return
      }
      const selection = quill.getSelection(true)
      const position = selection ? selection.index : Math.max(0, quill.getLength() - 1)
      quill.insertText(position, code, 'user')
      quill.setSelection(position + code.length, 0, 'silent')
    },
    enregistrer() {
      if (!this.formulaire.nom) {
        this.erreur = 'Indiquez le nom du certificat.'
        return
      }
      this.enregistrement = true
      this.erreur = ''
      return ModelesApi.enregistrer({ id: this.formulaire.id, nom: this.formulaire.nom, texte: this.formulaire.texte || '' }).then(() => {
        this.formulaire = null
        if (this.$toasted) this.$toasted.show('Modèle enregistré', { duration: 2500, theme: 'toasted-primary', type: 'success', position: 'top-center' })
        return this.charger()
      }).catch(erreur => {
        this.erreur = messageErreur(erreur, 'L’enregistrement a échoué.')
      }).then(() => {
        this.enregistrement = false
      })
    },
    deplacer(index, sens) {
      const ids = this.modeles.map(m => m.id)
      const cible = index + sens
      if (cible < 0 || cible >= ids.length) return
      ids.splice(cible, 0, ids.splice(index, 1)[0])
      this.occupe = true
      return ModelesApi.reordonner(ids).then(reponse => {
        this.modeles = Array.isArray(reponse) ? reponse : this.modeles
      }).catch(erreur => {
        this.erreur = messageErreur(erreur, 'Le nouvel ordre n’a pas pu être enregistré.')
      }).then(() => {
        this.occupe = false
      })
    },
    retirer(modele) {
      if (this.aRetirer !== modele.id) {
        this.aRetirer = modele.id
        return
      }
      this.aRetirer = null
      this.occupe = true
      return ModelesApi.retirer(modele.id).then(() => this.charger()).catch(erreur => {
        this.erreur = messageErreur(erreur, 'Le modèle n’a pas pu être retiré.')
      }).then(() => {
        this.occupe = false
      })
    },
  },
}
</script>

<style scoped>
.cm-entete { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; flex-wrap: wrap }
.cm-entete p { max-width: 720px }
.cm-liste { list-style: none; margin: 16px 0 0; padding: 0 }
.cm-liste li { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 12px 14px; border: 1px solid #e3eaf7; border-radius: 12px; margin-bottom: 8px; background: #fff }
.cm-infos { display: flex; flex-direction: column; gap: 2px; min-width: 0 }
.cm-infos span { font-size: 13px; overflow: hidden; text-overflow: ellipsis }
.cm-vide { font-size: 13px; color: #b07d2b }
.cm-actions { display: flex; align-items: center; gap: 6px; flex: 0 0 auto }
.cm-ordre { width: 32px; height: 32px; border: 1px solid #d7e3fa; border-radius: 8px; background: #fff; color: var(--ui-text, #304269) }
.cm-ordre:disabled { opacity: .4 }
.cm-fenetre { position: fixed; z-index: 1050; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(30, 40, 64, .45); padding: 16px }
.cm-fenetre-contenu { display: flex; flex-direction: column; width: 94vw; max-width: 1380px; max-height: 94vh; background: #fff; border-radius: 16px; overflow: hidden }
.cm-fenetre-entete { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 18px 24px; border-bottom: 1px solid #edf0f6 }
.cm-fenetre-entete h4 { font-size: 22px; line-height: 1.3 }
.cm-fermer { font-size: 24px; line-height: 1 }
.cm-corps { padding: 24px 32px; background: #f7f9fe; overflow-y: auto }
.cm-corps .form-group label { display: block }
.cm-champs { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 12px; font-size: 14px; color: var(--ui-text-secondary, #65728b) }
.cm-champs button { border: 1px solid #c9d7fc; border-radius: 999px; padding: 4px 12px; background: #fff; color: #3369e8; font-weight: 600; font-size: 13px }
.cm-champs button:hover { background: #edf3ff; border-color: #5d84f6 }
.cm-editeur { background: #fff; border: 1px solid #dce5fb; border-radius: 12px; padding: 14px; box-shadow: 0 5px 20px rgba(29, 64, 135, .05) }
.cm-editeur ::v-deep .ql-toolbar.ql-snow { border-color: #d2ddf5; border-radius: 8px 8px 0 0; background: #f4f7fd }
.cm-editeur ::v-deep .ql-container.ql-snow { border-color: #d2ddf5; border-radius: 0 0 8px 8px; background: #fff }
.cm-editeur ::v-deep .ql-editor { min-height: 380px; font-size: 17px; line-height: 1.55; padding: 22px 26px }
.cm-pied { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 24px; border-top: 1px solid #edf0f6 }
@media (max-width: 700px) { .cm-liste li { flex-direction: column; align-items: stretch } .cm-corps { padding: 16px } }
</style>
